import React, { useCallback, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  makeStyles
} from '@material-ui/core'
import PropTypes from 'prop-types'
import { isEmpty, noop } from 'lodash'
import ListContainer from '../../../molecules/ListContainer'
import {
  CSV_CELL_TYPES,
  ICON_NAMES,
  TABLE_VARIANTS,
  TEXT_ALIGNMENTS,
  TEXT_VARIANTS
} from '../../../../constants'
import { TableVirtualizedWithDynamicHeight } from '../../../molecules/Table'
import EmptySection from '../../../atoms/EmptySection'
import CheckBoxList from '../../../organisms/CheckBoxList'
import Text from '../../../atoms/Text'
import Avatar from '../../../atoms/Avatar'
import Icon from '../../../atoms/Icon'
import { useToggle } from '../../../../hooks'
import SearchBarInput from '../../../molecules/SearchBar/SearchBarInput'

const LABELS_COMMON_PROPS = {
  alignment: TEXT_ALIGNMENTS.left,
  colSpan: 1,
  variant: TEXT_VARIANTS.h3
}

const ACCOUNTS_LABELS = [
  {
    ...LABELS_COMMON_PROPS,
    name: 'Name'
  },
  {
    ...LABELS_COMMON_PROPS,
    alignment: TEXT_ALIGNMENTS.right,
    name: 'Number'
  }
]

const useStyles = makeStyles((theme) => ({
  column: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  spaceDivider: {
    minHeight: '1rem',
    width: '100%'
  },
  title: ({ editMode }) => ({
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'end',
    marginBottom: editMode ? theme.spacing(2) : 0
  }),
  titleAdornment: {
    display: 'flex',
    color: '#9296A5',
    flexDirection: 'row',
    marginLeft: '0.875rem',
    '& > span': {
      color: '#5A5E6D',
      marginLeft: '0.25rem'
    }
  },
  columnTitle: {
    textAlign: 'left',
    fontSize: '1.5rem'
  },
  checkBoxList: {
    maxHeight: '20rem',
    overflowY: 'auto'
  },
  checkBoxItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%'
  },
  checkboxItemTitle: {
    fontWeight: '600',
    fontSize: '1rem',
    color: '#141929'
  },
  checkboxItemDetails: {
    fontSize: '0.875rem',
    color: '#141929',
    marginLeft: 'auto'
  },
  checkboxItemAvatar: {
    marginRight: '0.5rem'
  },
  addAccountsContainer: {
    height: 'unset !important'
  },
  selectedItemContainer: {
    display: 'flex',
    alignItems: 'center',
    border: '2px solid #EEF0F8',
    borderRadius: '6px',
    padding: '0.75rem 1rem'
  }
}))

const renderAccountRowRightItem = (shortNumber, onRemoveAccount) => {
  return (
    <Box display='flex' flexDirection='row' alignItems='center'>
      {shortNumber}
      {onRemoveAccount && (
        <Box ml='1rem' style={{ cursor: 'pointer' }} onClick={onRemoveAccount}>
          <Icon
            name={ICON_NAMES.removeCircle}
            color='#D44333'
            customSize='1.25rem'
          />
        </Box>
      )}
    </Box>
  )
}

const mapAccountRows = (accounts, editMode, onAssignUnAssignAccount) => {
  return accounts.map((account) => {
    const { id, name, imageUrl, shortNumber } = account
    return [
      {
        alignment: TEXT_ALIGNMENTS.left,
        avatarSource: imageUrl,
        value: name,
        withAvatar: true,
        isPublicAvatar: true,
        csvData: { type: CSV_CELL_TYPES.string }
      },
      {
        alignment: TEXT_ALIGNMENTS.right,
        value: editMode
          ? renderAccountRowRightItem(shortNumber, () =>
            onAssignUnAssignAccount({
              accountId: id,
              isAssign: false,
              account: account
            })
          )
          : shortNumber,
        valueId: id,
        csvData: { type: CSV_CELL_TYPES.string }
      }
    ]
  })
}

const ClientAccountsAssignment = ({
  editMode,
  selectedAccounts,
  accountsAssigned,
  onSearchAccounts,
  accountsUnAssigned,
  searchResultOptions,
  onAssignUnAssignAccount,
  showAssignedAccountsTable
}) => {
  const classes = useStyles({ editMode })
  const [searchMode, , toggleSearchModeOn, toggleSearchModeOff] = useToggle()

  const escFunction = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        toggleSearchModeOff()
      }
    },
    [toggleSearchModeOff]
  )

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false)
    return () => {
      document.removeEventListener('keydown', escFunction, false)
    }
  }, [escFunction])

  const accountsAssignedRows = useMemo(() => {
    if (!editMode) {
      return mapAccountRows(accountsAssigned.data, false)
    }
    const stagingAccounts = [
      ...selectedAccounts.unAssigned,
      ...selectedAccounts.assigned
    ].reduce(
      (acc, account) => ({
        ...acc,
        [account.id]: { ...account }
      }),
      {}
    )
    const accounts = accountsAssigned.data.filter(
      ({ id }) => !stagingAccounts[id]
    )
    const accountRows = mapAccountRows(
      accounts,
      true,
      onAssignUnAssignAccount
    )
    return accountRows
  }, [
    editMode,
    selectedAccounts,
    accountsAssigned.data,
    onAssignUnAssignAccount
  ])

  const renderCheckboxBody = useCallback(
    (_, { name, shortNumber, imageUrl }) => {
      return (
        <div className={classes.checkBoxItem}>
          <Avatar
            src={imageUrl}
            customSize='2rem'
            avatarLetters={name}
            customClassName={classes.checkboxItemAvatar}
          />
          <Text text={name} className={classes.checkboxItemTitle} />
          <Text text={shortNumber} className={classes.checkboxItemDetails} />
        </div>
      )
    },
    [
      classes.checkBoxItem,
      classes.checkboxItemTitle,
      classes.checkboxItemAvatar,
      classes.checkboxItemDetails
    ]
  )

  const renderUnAssignedAccountsChecked = useMemo(() => {
    const accounts = selectedAccounts.assigned
    return accounts.map((account, index) => {
      const isLastItem = accounts.length - 1 === index
      const { id, name, imageUrl, shortNumber } = account

      return (
        <React.Fragment key={id}>
          <div className={classes.selectedItemContainer}>
            <Avatar customSize='2rem' src={imageUrl} avatarLetters={name} />
            <Box ml='1rem'>
              <Text text={name} customFontWeight='600' />
            </Box>
            <Box ml='auto'>
              {renderAccountRowRightItem(shortNumber, () =>
                onAssignUnAssignAccount({
                  account,
                  accountId: id,
                  isAssign: false
                })
              )}
            </Box>
          </div>
          {!isLastItem && <div className={classes.spaceDivider} />}
        </React.Fragment>
      )
    })
  }, [
    classes.spaceDivider,
    onAssignUnAssignAccount,
    selectedAccounts.assigned,
    classes.selectedItemContainer
  ])

  const onClickAccount = useCallback(
    (_, row) => {
      if (editMode) return
      const rowConfig = row[row.length - 1] || {}
      const url = `${window.location.origin}/admin/accounts/${rowConfig?.valueId}`
      window.open(url, '_blank')
    },
    [editMode]
  )

  const renderAccountsAssignedTable = useMemo(() => {
    const accountsTable = (
      <TableVirtualizedWithDynamicHeight
        rows={accountsAssignedRows}
        onRowClick={onClickAccount}
        elementType='div'
        minHeight='30rem'
        virtualized
        variant={TABLE_VARIANTS.floatingHeader}
        labels={ACCOUNTS_LABELS}
        isLoading={accountsAssigned.loading}
        emptySection={
          <EmptySection
            title='No Accounts Assigned Yet'
            description='Use the search bar above to add accounts. Once you’ve added
accounts, they will appear here.'
            styles={{ height: '100%' }}
          />
        }
      />
    )
    if (editMode) {
      return (
        <ListContainer title='ASSIGNED ACCOUNTS'>{accountsTable}</ListContainer>
      )
    }
    return accountsTable
  }, [
    accountsAssignedRows,
    accountsAssigned,
    onClickAccount,
    editMode
  ])

  const renderAddAccountsBody = useMemo(() => {
    if (searchMode) {
      if (!isEmpty(searchResultOptions)) {
        return (
          <CheckBoxList
            defaultItems={searchResultOptions}
            onItemChange={(id, checked, payload) =>
              onAssignUnAssignAccount({
                accountId: id,
                isAssign: checked,
                account: payload
              })}
            renderCheckboxBody={renderCheckboxBody}
          />
        )
      } else if (accountsUnAssigned.isEmpty) {
        return (
          <EmptySection
            title='No Accounts Found'
            description=''
            extraStyles={{ padding: '1rem' }}
          />
        )
      }
    }
    return renderUnAssignedAccountsChecked
  }, [
    searchMode,
    searchResultOptions,
    renderCheckboxBody,
    onAssignUnAssignAccount,
    accountsUnAssigned.isEmpty,
    renderUnAssignedAccountsChecked
  ])

  return (
    <div className={classes.column}>
      <div className={classes.title}>
        <Typography variant={TEXT_VARIANTS.h3} className={classes.columnTitle}>
          Accounts
        </Typography>
        <div className={classes.titleAdornment}>
          <Text text={accountsAssigned.total || '0'} />
          <span>
            <Text text='Total' />
          </span>
        </div>
      </div>
      {editMode && (
        <ListContainer
          title='ADD ACCOUNTS'
          onClickAway={toggleSearchModeOff}
          containerClassName={classes.addAccountsContainer}
        >
          <>
            <SearchBarInput
              onChange={onSearchAccounts}
              onFocus={toggleSearchModeOn}
              loading={accountsUnAssigned.loading}
              placeholder='Search by account name or number'
            />
            <div className={classes.checkBoxList}>{renderAddAccountsBody}</div>
          </>
        </ListContainer>
      )}
      <div className={classes.spaceDivider} />
      {showAssignedAccountsTable && renderAccountsAssignedTable}
    </div>
  )
}

const accountsShape = PropTypes.shape({
  data: PropTypes.array,
  loading: PropTypes.bool,
  total: PropTypes.number,
  isEmpty: PropTypes.bool
})

const accountsShapeDefaults = {
  data: [],
  loading: false,
  total: 0
}

ClientAccountsAssignment.propTypes = {
  editMode: PropTypes.bool,
  onSearchAccounts: PropTypes.func,
  selectedAccounts: PropTypes.shape({
    assigned: PropTypes.array,
    unAssigned: PropTypes.array
  }),
  accountsAssigned: accountsShape,
  accountsUnAssigned: accountsShape,
  onAssignUnAssignAccount: PropTypes.func,
  searchResultOptions: PropTypes.array,
  showAssignedAccountsTable: PropTypes.bool
}

ClientAccountsAssignment.defaultProps = {
  editMode: false,
  selectedAccounts: {
    assigned: [],
    unAssigned: []
  },
  onSearchAccounts: noop,
  onAssignUnAssignAccount: noop,
  accountsAssigned: { ...accountsShapeDefaults },
  accountsUnAssigned: { ...accountsShapeDefaults },
  searchResultOptions: [],
  showAssignedAccountsTable: true
}

export default ClientAccountsAssignment
